import React, { useEffect, useState } from 'react';
import { McModal } from '@maersk-global/mds-react-wrapper/mc-modal';
import { McLoadingIndicator } from '@maersk-global/mds-react-wrapper/mc-loading-indicator';
import { McButton } from '@maersk-global/mds-react-wrapper';
import { API_URL } from '../../services/api-config';

// Printable export helpers
const escapeHtml = (unsafe) => {
  if (unsafe == null) return '';
  return String(unsafe)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

// Convert HTML email body to plain text with proper table formatting
const htmlToText = (html) => {
  if (!html || typeof html !== 'string') return '';
  
  // Check if the string contains HTML tags
  const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(html);
  if (!hasHtmlTags) return html; // Return as-is if no HTML tags
  
  try {
    // Pre-process: Remove style tags, script tags, HTML comments, and head section
    let cleanedHtml = html
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '') // Remove style tags and content
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '') // Remove script tags and content
      .replace(/<!--[\s\S]*?-->/g, '') // Remove HTML comments
      .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, ''); // Remove head section entirely
    
    // Create a temporary DOM element to parse HTML
    const doc = new DOMParser().parseFromString(cleanedHtml, 'text/html');
    
    // Process tables separately for better formatting
    const tables = doc.body.querySelectorAll('table');
    tables.forEach(table => {
      const tableText = [];
      const rows = table.querySelectorAll('tr');
      
      rows.forEach((row, rowIndex) => {
        const cells = row.querySelectorAll('th, td');
        const cellTexts = [];
        
        cells.forEach(cell => {
          const cellText = cell.textContent.trim().replace(/\s+/g, ' ');
          if (cellText) cellTexts.push(cellText);
        });
        
        if (cellTexts.length > 0) {
          // Join cells with pipe separator for readability
          tableText.push(cellTexts.join(' | '));
        }
      });
      
      // Replace table with formatted text
      if (tableText.length > 0) {
        const replacement = doc.createTextNode('\n' + tableText.join('\n') + '\n');
        table.parentNode.replaceChild(replacement, table);
      }
    });
    
    // Remove image tags
    const images = doc.body.querySelectorAll('img');
    images.forEach(img => img.remove());
    
    // Process line breaks: replace <br> with actual line breaks before extracting text
    const brs = doc.body.querySelectorAll('br');
    brs.forEach(br => {
      const lineBreak = doc.createTextNode('\n');
      br.parentNode.replaceChild(lineBreak, br);
    });
    
    // Extract textContent (removes HTML, keeps only readable text)
    let text = doc.body.textContent || '';
    
    // Clean up excessive whitespace and formatting
    text = text
      .replace(/\r\n/g, '\n') // Normalize line breaks
      .replace(/\n{4,}/g, '\n\n') // Reduce multiple line breaks to max 2
      .replace(/[ \t]+/g, ' ') // Reduce multiple spaces to single space
      .replace(/\n /g, '\n') // Remove spaces at start of lines
      .replace(/ \n/g, '\n') // Remove spaces at end of lines
      .trim();
    
    return text;
  } catch (error) {
    console.error('Failed to parse HTML:', error);
    // Fallback: strip tags with regex (less reliable but works)
    return html
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
      .replace(/<!--[\s\S]*?-->/g, '')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/tr>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/\n\s+/g, '\n')
      .trim();
  }
};

// Export messages by generating a PDF in-place (no new tab). This creates an off-screen
// iframe with a full HTML document (srcdoc), loads html2pdf inside the iframe, runs
// the PDF generation there and triggers the download. Returns a promise that resolves
// when the download has been triggered. Caller is responsible for closing the modal
// after the promise resolves.
const exportMessagesAsPDF = async (caseNumber, messages = []) => {
  if (!messages || messages.length === 0) return;
  if (!caseNumber) return;

  try {
    // Build body content
    const parts = [];
    parts.push(`<h2>Case ${escapeHtml(caseNumber)} - Exported Messages</h2>`);
    messages.forEach((m) => {
      parts.push('<div class="msg">');
      // parts.push(`<div class="hdr"><strong>From:</strong> ${escapeHtml(m.from ?? m.sender ?? 'N/A')}</div>`);
      // parts.push(`<div class="hdr"><strong>To:</strong> ${escapeHtml(m.to ?? m.to_recipients ?? 'N/A')}</div>`);
      // parts.push(`<div class="hdr"><strong>Subject:</strong> ${escapeHtml(m.subject ?? 'N/A')}</div>`);
      const bodyText = htmlToText(String(m.body ?? ''));
      parts.push(`<div class="body">${escapeHtml(bodyText).replace(/\n/g, '<br/>')}</div>`);
      const received = m.receive_timestamp ? m.receive_timestamp.replace('T', ' ').split('.')[0] : (m.createddate ? m.createddate.replace('T', ' ').split('.')[0] : '');
      parts.push(`<div class="footer"><em>Received: ${escapeHtml(received)}</em></div>`);
      parts.push('</div>');
    });

    // Style included directly in the full HTML to avoid sharing constructed stylesheets
    const style = '<style>body{font-family: Arial, Helvetica, sans-serif; padding:20px; color:#111} .msg{border:1px solid #e6e6e6;padding:12px;border-radius:6px;margin-bottom:12px;} .hdr{font-size:13px;color:#333;margin-bottom:6px;} .body{font-size:12px;color:#444;white-space:pre-wrap;margin-top:6px;} .footer{font-size:11px;color:#777;margin-top:8px;}</style>';

    const fullHtml = '<!doctype html><html><head><meta charset="utf-8"><title>Exported Emails</title>' + style + '</head><body>' + parts.join('') + '</body></html>';

    // Create off-screen iframe and set srcdoc so iframe has its own document context
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.left = '-10000px';
    iframe.style.top = '0';
    iframe.style.width = '800px';
    iframe.style.height = '1000px';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.srcdoc = fullHtml;
    document.body.appendChild(iframe);

    // Wait for the iframe to be ready
    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Iframe load timed out')), 10000);
      iframe.onload = () => {
        clearTimeout(timeout);
        resolve();
      };
    });

    // Inject html2pdf into the iframe by fetching the bundle in the parent
    // window and writing it as an inline script into the iframe document.
    // This avoids relying on the iframe to load external scripts (CSP/srcdoc issues).
    await new Promise(async (resolve, reject) => {
      try {
        const iDoc = iframe.contentDocument || iframe.contentWindow.document;

        // Try to reuse cached bundle if available on the window
        let bundleCode = window.__html2pdf_bundle_code;
        if (!bundleCode) {
          const bundleUrl = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
          const resp = await fetch(bundleUrl);
          if (!resp.ok) throw new Error(`Failed to fetch html2pdf bundle: ${resp.status}`);
          bundleCode = await resp.text();
          // cache for future calls in this page
          try { window.__html2pdf_bundle_code = bundleCode; } catch (e) { /* ignore */ }
        }

        // Create inline script in iframe
        const script = iDoc.createElement('script');
        script.type = 'text/javascript';
        script.text = bundleCode;
        iDoc.head.appendChild(script);

        // html2pdf should be available synchronously after executing the script
        const maxWait = 5000;
        const start = Date.now();
        const check = () => {
          try {
            if (iframe.contentWindow && typeof iframe.contentWindow.html2pdf === 'function') return resolve();
          } catch (e) {
            // ignore
          }
          if (Date.now() - start > maxWait) return reject(new Error('html2pdf did not initialize inside iframe')); 
          setTimeout(check, 50);
        };
        check();
      } catch (e) {
        reject(e);
      }
    });

    const opt = {
      margin:       10,
      filename:     `Case-${caseNumber}-Messages.pdf`,
      image:        { type: 'jpeg', quality: 0.98 },
      html2canvas:  { scale: 2 },
      jsPDF:        { unit: 'pt', format: 'a4', orientation: 'portrait' }
    };

    // Instead of saving directly from the iframe (which may trigger downloads
    // in a way we can't control), generate the PDF inside the iframe and
    // post the resulting Blob back to the parent via postMessage. The parent
    // will then trigger the download and only after that we cleanup the iframe.

    const blobPromise = new Promise((resolve, reject) => {
      let settled = false;
      const onMessage = (e) => {
        if (e.source !== iframe.contentWindow) return;
        const data = e.data || {};
        if (data && data.type === 'html2pdf:blob' && data.blob) {
          if (settled) return;
          settled = true;
          window.removeEventListener('message', onMessage);
          resolve(data.blob);
        } else if (data && data.type === 'html2pdf:error') {
          if (settled) return;
          settled = true;
          window.removeEventListener('message', onMessage);
          reject(new Error(data.message || 'Unknown error in iframe'));
        }
      };
      window.addEventListener('message', onMessage);
      setTimeout(() => {
        if (settled) return;
        settled = true;
        window.removeEventListener('message', onMessage);
        reject(new Error('Timed out waiting for PDF blob from iframe'));
      }, 20000);
    });

    // Inject generator script into iframe to create the PDF blob and postMessage it
    try {
      const iDoc = iframe.contentDocument || iframe.contentWindow.document;
      const genScript = iDoc.createElement('script');
      genScript.type = 'text/javascript';
      genScript.text = "(function(){try{var opt = " + JSON.stringify(opt) + "; html2pdf().from(document.body).set(opt).toPdf().get('pdf').then(function(pdf){ try{ var blob = pdf.output('blob'); parent.postMessage({type:'html2pdf:blob', blob: blob}, '*'); }catch(e){ parent.postMessage({type:'html2pdf:error', message: e && e.message ? e.message : String(e)}, '*'); } }).catch(function(err){ parent.postMessage({type:'html2pdf:error', message: err && err.message ? err.message : String(err)}, '*'); }); }catch(e){ parent.postMessage({type:'html2pdf:error', message: e && e.message ? e.message : String(e)}, '*'); }})();";
      iDoc.body.appendChild(genScript);
    } catch (e) {
      try { document.body.removeChild(iframe); } catch (er) { /* ignore */ }
      throw e;
    }

    // Wait for blob from iframe
    const blob = await blobPromise;

    // Trigger download in parent from received blob
    try {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = opt.filename || `Case-${caseNumber}-Messages.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Failed to trigger download from blob', e);
      throw e;
    }

    // cleanup
    try { document.body.removeChild(iframe); } catch (e) { /* ignore */ }

    return;
  } catch (err) {
    console.error('Export Emails failed', err);
    alert('Failed to export emails: ' + (err.message || String(err)));
  }
};

const MessagesModal = ({ open, onClose, caseNumber, initialData }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(initialData ?? null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let mounted = true;
    const fetchMessages = async () => {
      if (!open) return;
      // If initialData provided and has messages, skip fetch
      if (initialData && initialData.messages) return;
      if (!caseNumber) return;
      try {
        setLoading(true);
        setData(null);
        const resp = await fetch(`${API_URL}/get_case_messages?casenumber=${encodeURIComponent(caseNumber)}`);
        if (!resp.ok) {
          const text = await resp.text().catch(() => resp.statusText || 'error');
          throw new Error(`Failed to fetch messages: ${resp.status} ${text}`);
        }
        const json = await resp.json();
        if (mounted) setData({ casenumber: caseNumber, messages: json.messages || [], error: json.error });
      } catch (err) {
        if (mounted) setData({ casenumber: caseNumber, messages: [], error: err.message || String(err) });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchMessages();
    return () => { mounted = false; };
  }, [open, caseNumber]);

  const effectiveData = initialData?.casenumber === caseNumber ? initialData : data;

  return (
    <McModal
      heading={`Case Number - ${caseNumber || ''}`}
      closed={() => { if (onClose) onClose(); }}
      open={open}
      width="50vw"
      height="60vh"
    >
      <div style={{ padding: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>{effectiveData?.casenumber ? `Case Number - ${effectiveData.casenumber}` : ''}</div>
          <div>
            {effectiveData && Array.isArray(effectiveData.messages) && effectiveData.messages.length > 0 && (
              <McButton
                label={exporting ? 'Exporting...' : 'Export Emails'}
                appearance="secondary"
                disabled={exporting}
                click={async () => {
                  try {
                    setExporting(true);
                    await exportMessagesAsPDF(effectiveData.casenumber, effectiveData.messages);
                    // Give browser a moment to trigger the download before closing
                    setTimeout(() => {
                      setExporting(false);
                      if (typeof onClose === 'function') onClose();
                    }, 300);
                  } catch (err) {
                    console.error('Export failed', err);
                    setExporting(false);
                  }
                }}
              />
            )}
          </div>
        </div>

        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <McLoadingIndicator appearance="primary" />
            <div style={{ fontSize: 12, color: '#666' }}>Loading messages...</div>
          </div>
        ) : effectiveData ? (
          <div>
            {effectiveData.error ? (
              <div style={{ color: 'red' }}>{effectiveData.error}</div>
            ) : effectiveData.messages && effectiveData.messages.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {effectiveData.messages.map((m, i) => {
                  const bodyText = htmlToText(String(m.body ?? ''));
                  return (
                    <div key={i} style={{ border: '1px solid #e6e6e6', padding: '8px', borderRadius: '4px', overflowY: 'auto', maxHeight: '120px' }}>
                      {/* <div style={{ fontSize: '12px', color: '#333' }}><strong>From:</strong> {m.from ?? m.sender ?? 'N/A'}</div>
                      <div style={{ fontSize: '12px', color: '#333' }}><strong>To:</strong> {m.to ?? m.to_recipients ?? 'N/A'}</div>
                      <div style={{ fontSize: '12px', color: '#333' }}><strong>Subject:</strong> {m.subject ?? 'N/A'}</div> */}
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '6px', whiteSpace: 'pre-wrap' }}>{bodyText}</div>
                      <div style={{ fontSize: '11px', color: '#999', marginTop: '6px' }}><em>Received: {m.receive_timestamp ? m.receive_timestamp.replace('T', ' ').split('.')[0] : (m.createddate ? m.createddate.replace('T', ' ').split('.')[0] : '')}</em></div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div>No messages available for this case.</div>
            )}
          </div>
        ) : (
          <div>No message details available.</div>
        )}
      </div>
    </McModal>
  );
};

export default MessagesModal;
